import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { IconArrowLeft } from '@tabler/icons-react';

import { Button, IconButton, Skeleton } from '@zendesk/forethought-elements';
import {
  AiInstructionForm,
  FooterProps,
} from 'src/pages/knowledge/ai-instruction-tab/AiInstructionForm';
import {
  useDiscardInstructionDraftMutation,
  useGetInstructionQuery,
  usePublishInstructionMutation,
  useUpdateInstructionMutation,
} from 'src/services/ai-instruction/aiInstructionApi';

const LoadingSkeletonView = () => {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: 40,
          }}
        >
          <Skeleton height={40} width={40} />
        </Box>
        <Box
          sx={{
            ml: 2,
          }}
        >
          <Skeleton height={32} width={240} />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Skeleton height={24} width={120} />
          </Box>
          <Skeleton height={40} width='60%' />
        </Box>
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Skeleton height={20} width={140} />
          </Box>
          <Skeleton height={100} width='100%' />
        </Box>
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Box
            sx={{
              mb: 1,
            }}
          >
            <Skeleton height={20} width={160} />
          </Box>
          <Skeleton height={100} width='100%' />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 'auto',
        }}
      >
        <Skeleton height={40} width={100} />
        <Skeleton height={40} width={200} />
      </Box>
    </>
  );
};

export const AiInstructionDetail = ({
  aiInstructionId,
  allowAddingContextVariables = false,
  FooterProps,
  header,
  numAiInstructionsWithContextVariables,
  onClose,
  publishCacheKey = 'instruction-form-publish',
}: {
  aiInstructionId: string;
  allowAddingContextVariables?: boolean;
  FooterProps?: { marginTop?: string };
  header?: React.ReactNode;
  numAiInstructionsWithContextVariables?: number;
  onClose: () => void;
  publishCacheKey?: string;
}) => {
  const { t } = useTranslation('workflow-builder-edit');
  const { data: existingInstruction, isLoading } = useGetInstructionQuery(
    { aiInstructionId },
    { skip: !aiInstructionId },
  );
  const [discardInstructionDraft] = useDiscardInstructionDraftMutation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {isLoading ? (
        <LoadingSkeletonView />
      ) : (
        <>
          {header ?? (
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <IconButton
                aria-label={t(
                  'customizationPanel.aiInstructions.closeFormAriaLabel',
                )}
                onClick={async () => {
                  await discardInstructionDraft({ aiInstructionId });
                  onClose();
                }}
                variant='ghost'
              >
                <IconArrowLeft color='black' />
              </IconButton>
              <Typography variant='font24'>
                {t('customizationPanel.aiInstructions.knowledgeCoaching')}
              </Typography>
            </Box>
          )}
          {existingInstruction && (
            <AiInstructionForm
              allowAddingContextVariables={allowAddingContextVariables}
              containerSx={{ p: 0 }}
              existingInstruction={existingInstruction}
              Footer={Footer}
              FooterProps={FooterProps}
              key={existingInstruction?.ai_instruction_id}
              numAiInstructionsWithContextVariables={
                numAiInstructionsWithContextVariables
              }
              onSuccess={onClose}
              publishCacheKey={publishCacheKey}
              shouldEditBrands={false}
              shouldEditProducts={false}
            />
          )}
        </>
      )}
    </Box>
  );
};

const Footer = ({
  isLoadingDebounce,
  isValid,
  marginTop = 'auto',
  onClose,
  publishCacheKey = 'instruction-form-publish',
  submitForm,
}: FooterProps) => {
  const { t } = useTranslation('workflow-builder-edit');
  const [, { isLoading: isLoadingUpdateInstruction }] =
    useUpdateInstructionMutation({ fixedCacheKey: 'instruction-form' });
  const [, { isLoading: isLoadingPublishMutation }] =
    usePublishInstructionMutation({
      fixedCacheKey: publishCacheKey,
    });

  const isLoading = isLoadingPublishMutation || isLoadingUpdateInstruction;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        mt: marginTop,
      }}
    >
      <Button onClick={onClose} size='large' variant='secondary'>
        {t('customizationPanel.aiInstructions.cancel')}
      </Button>
      <Button
        disabled={!isValid || isLoading || isLoadingDebounce}
        fullWidth
        isLoading={isLoading || isLoadingDebounce}
        onClick={submitForm}
        size='large'
      >
        {t('customizationPanel.aiInstructions.save')}
      </Button>
    </Box>
  );
};
